"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import ExpiryBadge from "@/components/patient/documents/expiry-badge";
import IdDocumentPreview from "@/components/patient/documents/id-document-preview";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";
import { DocumentSummary } from "@/types/document";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { getSignedUrl } from "@/api/auth/gcs";
import { documentQueryOptions } from "@/api/provider/user/patient/document-query";

// GCS signed URLs live ~15m; treat ours as fresh for 10m so React Query
// re-signs before expiry while the sidebar stays open.
const ID_SIGNED_URL_STALE_MS = 10 * 60 * 1000;
interface Props {
  patientId: string;
}

/**
 * Compact "most recent ID" preview for the patient sidebar. Mirrors the
 * Overview-tab Identification card but sized for the sidebar: a small thumbnail
 * that opens the full-screen viewer on click. Visibility is gated by the parent
 * (tenant has an "ID" document type) — this just renders the newest one.
 */
export default function IdentificationSection(t0) {
  const $ = _c(46);
  const {
    patientId
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = documentQueryOptions.idDocuments(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!patientId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: t4,
    isLoading
  } = useQuery(t3);
  let t5;
  if ($[5] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const idDocuments = t5;
  let t6;
  bb0: {
    if (idDocuments.length === 0) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[7] !== idDocuments) {
      t7 = [...idDocuments].sort(_temp);
      $[7] = idDocuments;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    t6 = t7[0];
  }
  const mostRecentId = t6;
  const [isOpen, setIsOpen] = useState(false);
  const t7 = mostRecentId?.filePath;
  let t8;
  if ($[9] !== t7) {
    t8 = ["gcs", "id-document", t7];
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== mostRecentId) {
    t9 = () => getSignedUrl(mostRecentId.filePath);
    $[11] = mostRecentId;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = !!mostRecentId;
  let t11;
  if ($[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10,
      staleTime: ID_SIGNED_URL_STALE_MS
    };
    $[13] = t10;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const {
    data: signedPreview,
    isLoading: isSigningPreview
  } = useQuery(t11);
  if (isLoading) {
    let t12;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PatientSidebarSectionSkeleton />;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    return t12;
  }
  if (!mostRecentId) {
    let t12;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <p className="text-caption text-muted-foreground">No ID on file</p>;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    return t12;
  }
  const previewUrl = signedPreview?.signedUrl;
  const t12 = mostRecentId as DocumentSummary;
  let t13;
  if ($[19] !== isSigningPreview || $[20] !== previewUrl || $[21] !== t12) {
    t13 = <IdDocumentPreview document={t12} signedUrl={previewUrl} isLoading={isSigningPreview} heightClassName="h-40" />;
    $[19] = isSigningPreview;
    $[20] = previewUrl;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setIsOpen(true);
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = `View identity document: ${mostRecentId.title}`;
  let t16;
  if ($[24] !== t15) {
    t16 = <button type="button" onClick={t14} aria-label={t15} className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring" />;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t13 || $[27] !== t16) {
    t17 = <div className="group relative w-full max-w-[14rem] overflow-hidden rounded-md border border-border bg-background-secondary">{t13}{t16}</div>;
    $[26] = t13;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== mostRecentId.docType) {
    t18 = formatToHumanReadable(mostRecentId.docType);
    $[29] = mostRecentId.docType;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== mostRecentId.createdDate) {
    t19 = mostRecentId.createdDate ? <span>{` · ${formatDate(mostRecentId.createdDate)}`}</span> : <span>{""}</span>;
    $[31] = mostRecentId.createdDate;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t18 || $[34] !== t19) {
    t20 = <p className="mt-1.5 truncate text-caption text-foreground-secondary">{t18}{t19}</p>;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== mostRecentId.expiryDate) {
    t21 = mostRecentId.expiryDate && <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1"><span className="text-caption text-foreground-secondary">Expires {formatDate(mostRecentId.expiryDate)}</span><ExpiryBadge expiryDate={mostRecentId.expiryDate} size="xs" onlyWhenNoteworthy={true} /></div>;
    $[36] = mostRecentId.expiryDate;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const t22 = mostRecentId as DocumentSummary;
  let t23;
  if ($[38] !== isOpen || $[39] !== t22) {
    t23 = <DocumentViewDialog isOpen={isOpen} onOpenChange={setIsOpen} document={t22} />;
    $[38] = isOpen;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t17 || $[42] !== t20 || $[43] !== t21 || $[44] !== t23) {
    t24 = <>{t17}{t20}{t21}{t23}</>;
    $[41] = t17;
    $[42] = t20;
    $[43] = t21;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return t24;
}
function _temp(a, b) {
  const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
  const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
  return bTime - aTime;
}